import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { App, Button, Collapse, Dropdown, Empty, Flex, Image, Input, MenuProps, message, Space, Spin, Tag, Table, Typography } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, TeamOutlined, ZoomInOutlined } from "@ant-design/icons";
import { BsThreeDotsVertical } from "react-icons/bs";
import { get } from "lodash";
import { useSearchParams } from "react-router-dom";
import axiosInstance, { endpoints } from "services/axios";
import { useBoolean } from "hooks/use-boolean";
import { IPartnerCompany, IPartnerEmployee } from "models";
import { partnerPhotoUrl } from "utils/constants";
import CompanyDialog from "./company-dialog";
import EmployeeDialog from "./employee-dialog";

const DEFAULT_AVATAR_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200">
      <rect width="200" height="200" fill="#f0f0f0"/>
      <circle cx="100" cy="80" r="35" fill="#bfbfbf"/>
      <ellipse cx="100" cy="175" rx="65" ry="55" fill="#bfbfbf"/>
    </svg>`
  );

const PartnersTab = () => {
  const { modal } = App.useApp();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const companyModalBool = useBoolean();
  const employeeModalBool = useBoolean();

  const {
    data: companiesData,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["partner-companies"],
    queryFn: async () => {
      axiosInstance.defaults.headers.common.Authorization = searchParams.get("token");
      return await axiosInstance.get(endpoints.partner.companies);
    },
  });

  const companies: IPartnerCompany[] = get(companiesData, "data.data", []);

  const companyList = useMemo(
    () => companies.map((c) => ({ value: c.id, key: c.id, label: c.name })),
    [companies]
  );

  const filteredCompanies = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return companies;
    return companies
      .map((c) => {
        const matchCompany = c.name?.toLowerCase().includes(q);
        const employees = (c.employees || []).filter((e) =>
          `${e.firstName || ""} ${e.lastName || ""} ${e.phoneNumber || ""}`.toLowerCase().includes(q)
        );
        if (matchCompany) return c;
        if (employees.length) return { ...c, employees };
        return null;
      })
      .filter(Boolean) as IPartnerCompany[];
  }, [companies, search]);

  const confirmDeleteCompany = (company: IPartnerCompany) => () => {
    modal.confirm({
      title: "Kompaniyani o'chirish",
      content: `"${company.name}" va uning barcha xodimlari o'chirilsinmi?`,
      okText: "Ha, o'chirish",
      cancelText: "Bekor qilish",
      okButtonProps: { danger: true },
      onOk: async () =>
        await axiosInstance
          .delete(endpoints.partner.companyDelete(company.id))
          .then(() => {
            refetch();
            message.success("Kompaniya o'chirildi!");
          })
          .catch(() => ""),
    });
  };

  const confirmDeleteEmployee = (employee: IPartnerEmployee) => () => {
    modal.confirm({
      title: "Xodimni o'chirish",
      content: "Ushbu xodim o'chirilsinmi?",
      okText: "Ha, o'chirish",
      cancelText: "Bekor qilish",
      okButtonProps: { danger: true },
      onOk: async () =>
        await axiosInstance
          .delete(endpoints.partner.employeeDelete(employee.id))
          .then(() => {
            refetch();
            message.success("Xodim o'chirildi!");
          })
          .catch(() => ""),
    });
  };

  const openAddEmployee = (companyId?: string) => {
    if (!companies.length) return message.warning("Avval kompaniya qo'shing!");
    employeeModalBool.onTrue({ companyId });
  };

  const employeeColumns = (company: IPartnerCompany) => [
    {
      title: "№",
      dataIndex: "no",
      render: (_: any, i: any, index: number) => index + 1,
      width: 50,
    },
    {
      title: "Xodim",
      key: "name",
      render: (_: any, e: IPartnerEmployee) => {
        const photoSrc = partnerPhotoUrl(e.photoId) || DEFAULT_AVATAR_IMG;
        return (
          <Flex align="center" gap={10}>
            <Image
              width={38}
              height={38}
              className="rounded-full object-cover"
              wrapperClassName="rounded-full overflow-hidden"
              src={photoSrc}
              preview={{
                src: photoSrc,
                mask: <ZoomInOutlined style={{ fontSize: 16 }} />,
              }}
            />
            <span className="font-medium">
              {e.firstName} {e.lastName}
            </span>
            {e.primary && <Tag color="gold">Asosiy</Tag>}
          </Flex>
        );
      },
    },
    {
      title: "Telefon",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      render: (value: string) => value || "—",
    },
    {
      title: "",
      key: "actions",
      align: "right" as "right",
      width: 50,
      render: (_: any, e: IPartnerEmployee) => {
        const items: MenuProps["items"] = [
          {
            key: "edit",
            label: "Tahrirlash",
            icon: <EditOutlined />,
            onClick: () => employeeModalBool.onTrue({ employee: { ...e, companyId: company.id } }),
          },
          {
            key: "delete",
            label: "O'chirish",
            icon: <DeleteOutlined />,
            danger: true,
            onClick: confirmDeleteEmployee(e),
          },
        ];
        return (
          <Dropdown menu={{ items }} placement="bottomRight">
            <Button size="small" type="text" icon={<BsThreeDotsVertical />} />
          </Dropdown>
        );
      },
    },
  ];

  const collapseItems = filteredCompanies.map((c) => {
    const employees = c.employees || [];
    const primaryCount = employees.filter((e) => e.primary).length;

    const companyMenuItems: MenuProps["items"] = [
      { key: "edit", label: "Nomini tahrirlash", icon: <EditOutlined />, onClick: () => companyModalBool.onTrue(c) },
      { key: "delete", label: "Kompaniyani o'chirish", icon: <DeleteOutlined />, danger: true, onClick: confirmDeleteCompany(c) },
    ];

    return {
      key: c.id,
      label: (
        <Flex align="center" gap={10}>
          <span className="font-semibold">{c.name}</span>
          <Tag icon={<TeamOutlined />} color="blue">
            {employees.length} xodim
          </Tag>
          {primaryCount > 0 && <Tag color="gold">{primaryCount} asosiy</Tag>}
        </Flex>
      ),
      extra: (
        <Space onClick={(e) => e.stopPropagation()}>
          <Button size="small" onClick={() => openAddEmployee(c.id)}>
            + Xodim
          </Button>
          <Dropdown menu={{ items: companyMenuItems }} placement="bottomRight">
            <Button size="small" type="text" icon={<BsThreeDotsVertical />} />
          </Dropdown>
        </Space>
      ),
      children: (
        <Table
          bordered
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={employees}
          columns={employeeColumns(c)}
          locale={{ emptyText: "Bu kompaniyada xodim yo'q" }}
        />
      ),
    };
  });

  return (
    <div>
      <Flex align="center" justify="space-between" className="mb-3" gap={12} wrap="wrap">
        <Typography.Title className="mt-0" level={4}>
          Hamkorlar
        </Typography.Title>
        <Flex gap={8} wrap="wrap">
          <Input.Search
            placeholder="Kompaniya yoki xodim qidirish..."
            allowClear
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 260 }}
          />
          <Button onClick={() => openAddEmployee()}>+ Xodim</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => companyModalBool.onTrue(undefined)}>
            Kompaniya qo'shish
          </Button>
        </Flex>
      </Flex>

      {isFetching && !companiesData ? (
        <Flex justify="center" className="py-10">
          <Spin />
        </Flex>
      ) : !companies.length ? (
        <Empty description="Hozircha kompaniyalar yo'q">
          <Button type="primary" icon={<PlusOutlined />} onClick={() => companyModalBool.onTrue(undefined)}>
            Kompaniya qo'shish
          </Button>
        </Empty>
      ) : !filteredCompanies.length ? (
        <Empty description="Hech narsa topilmadi" />
      ) : (
        <Collapse
          bordered
          defaultActiveKey={filteredCompanies.map((c) => c.id)}
          items={collapseItems}
        />
      )}

      <CompanyDialog modalBool={companyModalBool} refetch={refetch} />
      <EmployeeDialog modalBool={employeeModalBool} refetch={refetch} companyList={companyList} />
    </div>
  );
};

export default PartnersTab;
